import { ConfirmDialog } from "../../../components/ui";

const SHOWN = 8;

/** A permission in the words the catalog uses; the key only when there is no label. */
export const permissionWord = (permission) => permission?.label || permission?.name || "Permission";

/** Sensitive permissions first, then by name, so the ones that matter are never the ones cut off. */
export function orderChanges(permissions = []) {
  return [...permissions].sort((a, b) => (
    Number(Boolean(b?.isSensitive)) - Number(Boolean(a?.isSensitive)) || permissionWord(a).localeCompare(permissionWord(b))
  ));
}

function ChangeList({ title, permissions }) {
  if (!permissions.length) return null;
  const ordered = orderChanges(permissions);
  const rest = ordered.length - SHOWN;
  return (
    <section>
      <h3 className="text-label text-foreground">{title} ({ordered.length})</h3>
      <ul className="mt-1 space-y-0.5">
        {ordered.slice(0, SHOWN).map((permission) => (
          <li key={permission.id} className="flex items-baseline justify-between gap-3">
            <span className="min-w-0 break-words text-foreground">{permissionWord(permission)}</span>
            {permission.isSensitive ? <span className="shrink-0 text-meta text-warning">Sensitive</span> : null}
          </li>
        ))}
      </ul>
      {rest > 0 ? <p className="mt-1 text-meta text-tertiary">and {rest} more</p> : null}
    </section>
  );
}

/**
 * RolePermissionChangeDialog (APP-RBAC-005 / 006, hr-revamp/49). Changing what a role allows changes the access of
 * everyone who holds it (C3), so the confirmation names the permissions being added and removed — not two counts.
 * Only the business owner reaches it; the server decides and records the change with the same lists.
 */
export default function RolePermissionChangeDialog({ roleName, added = [], removed = [], onConfirm, onClose }) {
  return (
    <ConfirmDialog
      title={`Change what ${roleName} can do?`}
      confirmLabel="Change permissions"
      cancelLabel="Keep editing"
      tone={removed.length && !added.length ? "danger" : "default"}
      consequence="Everyone with this role gets the change from their next action. It is recorded in the activity log with what was added and removed."
      failureTitle="Permissions weren't changed"
      errorFallback="The permissions couldn't be changed. Try again."
      onConfirm={onConfirm}
      onClose={onClose}
    >
      <div className="space-y-3">
        <ChangeList title="Added" permissions={added} />
        <ChangeList title="Removed" permissions={removed} />
      </div>
    </ConfirmDialog>
  );
}
